import React, { useState, useEffect, useContext } from "react";
import { Box } from "@bca/ui-library";

import { useCurrentFacet } from "../Hooks/useCurrentFacet.hook";
import useFacetList from "../Hooks/useFacetList.hook";
import { FacetType, MappedFacet } from "./filters.type";
import { enabledCategories, SingleSelectFacets } from "./filtersConfig";
import FiltersDesktopLoader from "./FiltersDesktopLoader";
import CheckboxGroup from "./CheckboxGroup";
import { ErrorContext } from "../context/ErrorContext";
import { AppliedCheckboxResult } from "../Hooks/useAppliedCheckBoxes";
import { PurchasesUrlQueryParams } from "../APICalls/buildGetPurchasesUrl";
import SingleSelectFacet from "./SingleSelectFacet";
import CollapsibleFacet from "./CollapsibleFacet";
import { useAuthZeroContext } from "../context/AuthZeroContext";

interface FilterCategoriesProps {
  purchasesUrlQueryParams?: PurchasesUrlQueryParams;
  updateAdditionalFilters?: any;
  setTotalResults?: React.Dispatch<React.SetStateAction<number>>;
  setIsFilterCategoryLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
  isToUseAuthZero?: boolean;
}

const getIsDisabled = (categoryKey, allQueryStrings) => {
  if (categoryKey !== "ModelGroup" && categoryKey !== "Model") {
    return !enabledCategories.multiselect.includes(categoryKey);
  }

  return !Boolean(allQueryStrings["bq"]?.includes("Make"));
};
const FilterCategories: React.FC<
  FilterCategoriesProps & AppliedCheckboxResult
> = ({
  appliedCheckboxes = [],
  setAppliedCheckboxesCheckBoxGroup,
  purchasesUrlQueryParams,
  updateAdditionalFilters,
  setTotalResults = () => {},
  setIsFilterCategoryLoading = () => {},
  isLoading = false,
  setAppliedCheckboxesSingleSelect,
  isToUseAuthZero
}) => {
  const {
    data: { authZeroAccessToken },
  } = useAuthZeroContext();
  const errorContext = useContext(ErrorContext);
  const [currentFacet, setCurrentFacet] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (query !== "") {
      setQuery("");
    }
  }, [currentFacet, query]);

  const { facetListResult, facetListError } = useFacetList(
    isToUseAuthZero,
    authZeroAccessToken
  );

  const filteredFacetList = facetListResult?.facetList;
  const {
    facetCounts: subfilters,
    facetCountsError: subFiltersFacetCountsError,
  } = useCurrentFacet(
    currentFacet as FacetType, 
    purchasesUrlQueryParams,
    isToUseAuthZero,
    authZeroAccessToken
  );
  if (facetListError || subFiltersFacetCountsError) {
    errorContext?.dispatch({ type: "filters" });
  }

  const getSingleSelectCounts = (updatedAppliedCheckboxes) => {
    let appliedCheckboxesTotal = null;

    const filteredCheckBoxes = updatedAppliedCheckboxes
      .filter((el) => el.categoryKey === currentFacet)
      .map((el) => el.filterKey);

    if (subfilters) {
      subfilters.counts
        .map((el) => el.filterValues)
        .flat()
        .filter((el) => filteredCheckBoxes.includes(el.name))
        .forEach((el) => (appliedCheckboxesTotal += el.count));
    }

    return appliedCheckboxesTotal;
  };

  const getMultipleSelectCounts = (updatedAppliedCheckboxes) => {
    const hasAtLeastOneModel = updatedAppliedCheckboxes.some(
      (el) => el.categoryKey === "Model"
    );
    const filterType = hasAtLeastOneModel ? "Model" : "Make";
    let appliedCheckboxesTotal = null;
    const filteredCheckBoxes = updatedAppliedCheckboxes
      .filter((el) => el.categoryKey === filterType)
      .map((el) => el.filterKey);

    if (subfilters) {
      subfilters.counts
        .map((el) => el.filterValues)
        .flat()
        .filter((el) => filteredCheckBoxes.includes(el.name))
        .forEach((el) => (appliedCheckboxesTotal += el.count));
    }
    return appliedCheckboxesTotal;
  };

  const getCounts = (updatedAppliedCheckboxes) => {
    if (SingleSelectFacets.includes(currentFacet)) {
      return getSingleSelectCounts(updatedAppliedCheckboxes);
    } else {
      return getMultipleSelectCounts(updatedAppliedCheckboxes);
    }
  };

  const setModalTotalResults = () => {
    const updatedAppliedCheckboxes = appliedCheckboxes.map((el) => el);
    const result = getCounts(updatedAppliedCheckboxes);
    const shouldShowTotalResults = result === null;
    setTotalResults(shouldShowTotalResults ? subfilters?.totalResults : result);
  };

  const stringifiedSubfilters = JSON.stringify(subfilters);
  const stringifiedAppliedCheckboxes = JSON.stringify(appliedCheckboxes);

  useEffect(
    setModalTotalResults,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      stringifiedSubfilters,
      stringifiedAppliedCheckboxes,
      purchasesUrlQueryParams.numberofDays,
    ]
  );

  let filteredValues = [];
  if (query?.length > 0) {
    subfilters.counts.forEach(({ filterKey, filterValues }) => {
      const filtered = filterValues.filter(({ name }) => {
        const subfiltersWords = name.toLowerCase().split(" ");

        return subfiltersWords.find((any) =>
          any.startsWith(query.toLowerCase())
        );
      });

      if (filtered?.length > 0) {
        filteredValues.push({ filterKey, filterValues: [...filtered] });
      }
    });
  }

  let filteredSubfilters =
    query?.length > 0 ? filteredValues : subfilters?.counts;

  const isInErrorState = () => {
    return errorContext?.state.somethingWentWrong;
  };

  useEffect(() => {
    if (Object.keys(purchasesUrlQueryParams.additionalFilters).length === 0) {
      setCurrentFacet(null);
    }
  }, [purchasesUrlQueryParams.additionalFilters]);
  const stringifiedFacetListResult = JSON.stringify(facetListResult);
  useEffect(() => {
    setIsFilterCategoryLoading(Boolean(!facetListResult));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifiedFacetListResult]);
  return (
    <>
      {!isInErrorState() && (
        <Box mt={5} mx={0}>
          {(!facetListResult || isLoading) && <FiltersDesktopLoader />}
          {filteredFacetList &&
            !isLoading &&
            filteredFacetList.map(
              ({ label, key: categoryKey, parentFilterKey }: MappedFacet) => {
                if (
                  categoryKey === "Eligibility" ||
                  categoryKey === "Payment"
                ) {
                  return (
                    <Box key={categoryKey}>
                      <CollapsibleFacet
                        categoryKey={categoryKey}
                        setCurrentFacet={setCurrentFacet}
                        currentFacet={currentFacet}
                        title={label}
                        isDisabled={getIsDisabled(
                          categoryKey,
                          purchasesUrlQueryParams.additionalFilters
                        )}
                      >
                        {enabledCategories.multiselect.includes(
                          currentFacet
                        ) && (
                          <SingleSelectFacet
                            label={label}
                            setAppliedCheckboxesSingleSelect={
                              setAppliedCheckboxesSingleSelect
                            }
                            filteredSubfilters={filteredSubfilters}
                            parentFilterKey={parentFilterKey}
                            categoryKey={currentFacet}
                            appliedCheckboxes={appliedCheckboxes}
                            updateAdditionalFilters={updateAdditionalFilters}
                            additionalFilters={
                              purchasesUrlQueryParams.additionalFilters
                            }
                          />
                        )}
                      </CollapsibleFacet>
                    </Box>
                  );
                }
                return (
                  <Box key={categoryKey}>
                    <CollapsibleFacet
                      categoryKey={categoryKey}
                      setCurrentFacet={setCurrentFacet}
                      currentFacet={currentFacet}
                      title={label}
                      isDisabled={getIsDisabled(
                        categoryKey,
                        purchasesUrlQueryParams.additionalFilters
                      )}
                    >
                      {enabledCategories.multiselect.includes(currentFacet) && (
                        <CheckboxGroup
                          filteredSubfilters={filteredSubfilters}
                          categoryKey={currentFacet}
                          setAppliedCheckboxesCheckBoxGroup={
                            setAppliedCheckboxesCheckBoxGroup
                          }
                          appliedCheckboxes={appliedCheckboxes}
                          parentFilterKey={parentFilterKey}
                          updateAdditionalFilters={updateAdditionalFilters}
                          additionalFilters={
                            purchasesUrlQueryParams.additionalFilters
                          }
                        />
                      )}
                    </CollapsibleFacet>
                  </Box>
                );
              }
            )}
        </Box>
      )}
    </>
  );
};

export default FilterCategories;
